import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../../utils/apiClient';
import { API_ENDPOINTS } from '../../config/api';
import { FaEthereum, FaPlus, FaEdit, FaTrash, FaCube, FaCoins } from 'react-icons/fa';
import ImageUploader from '../common/ImageUploader';

const AdminNFTs = ({ nftToEdit }) => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMintModal, setShowMintModal] = useState(false);
  const [currentNFT, setCurrentNFT] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    collectionId: '',
    name: '',
    description: '',
    image: '',
    rarity: 'Common',
    attributes: [
      { trait_type: 'Strength', value: 50 },
      { trait_type: 'Agility', value: 50 },
      { trait_type: 'Intelligence', value: 50 },
      { trait_type: 'Luck', value: 50 }
    ],
    price: 0.08,
    currency: 'ETH',
    status: 'draft'
  });

  // Mint form state
  const [mintData, setMintData] = useState({
    nftId: '',
    recipientAddress: ''
  });

  // Fetch collections and NFTs on component mount
  useEffect(() => {
    fetchCollections();
    fetchNFTs();
  }, []);

  // Handle nftToEdit prop - open edit modal when provided
  useEffect(() => {
    if (nftToEdit && nfts.length > 0) {
      const nftToEditObj = nfts.find(nft => nft._id === nftToEdit);
      if (nftToEditObj) {
        handleEdit(nftToEditObj);
      }
    }
  }, [nftToEdit, nfts]);

  // Fetch collections from API
  const fetchCollections = async () => {
    try {
      console.log('Fetching collections from endpoint:', API_ENDPOINTS.admin.nft.collections);
      const response = await api.get(API_ENDPOINTS.admin.nft.collections);
      console.log('Collections response:', response);

      // Check if response is an array or has a data property
      const collectionsData = Array.isArray(response) ? response :
                             (response.data ? response.data : []);

      console.log('Setting collections state with:', collectionsData);
      setCollections(collectionsData);
    } catch (error) {
      console.error('Error fetching NFT collections:', error);
      console.error('Error details:', error.message, error.response);
      toast.error('Failed to fetch NFT collections');
    }
  };

  // Fetch NFTs from API
  const fetchNFTs = async () => {
    try {
      setLoading(true);
      console.log('Fetching NFTs from endpoint:', API_ENDPOINTS.admin.nft.nfts);
      const response = await api.get(API_ENDPOINTS.admin.nft.nfts);
      console.log('NFTs response:', response);

      // Check if response is an array or has a data property
      const nftsData = Array.isArray(response) ? response :
                      (response.data ? response.data : []);

      console.log('Setting NFTs state with:', nftsData);
      setNfts(nftsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching NFTs:', error);
      console.error('Error details:', error.message, error.response);
      toast.error('Failed to fetch NFTs');
      setLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle mint form input change
  const handleMintInputChange = (e) => {
    const { name, value } = e.target;
    setMintData({
      ...mintData,
      [name]: value
    });
  };

  // Handle attribute change
  const handleAttributeChange = (index, field, value) => {
    const updatedAttributes = [...formData.attributes];
    updatedAttributes[index] = {
      ...updatedAttributes[index],
      [field]: field === 'value' ? Number(value) : value
    };
    setFormData({
      ...formData,
      attributes: updatedAttributes
    });
  };

  // Add new attribute
  const handleAddAttribute = () => {
    setFormData({
      ...formData,
      attributes: [
        ...formData.attributes,
        { trait_type: '', value: 0 }
      ]
    });
  };

  // Remove attribute
  const handleRemoveAttribute = (index) => {
    const updatedAttributes = [...formData.attributes];
    updatedAttributes.splice(index, 1);
    setFormData({
      ...formData,
      attributes: updatedAttributes
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      collectionId: selectedCollection || '',
      name: '',
      description: '',
      image: '',
      rarity: 'Common',
      attributes: [
        { trait_type: 'Strength', value: 50 },
        { trait_type: 'Agility', value: 50 },
        { trait_type: 'Intelligence', value: 50 },
        { trait_type: 'Luck', value: 50 }
      ],
      price: 0.08,
      currency: 'ETH',
      status: 'draft'
    });
    setCurrentNFT(null);
  };

  // Navigate to dedicated edit page
  const handleEdit = (nft) => {
    navigate(`/nft/${nft._id}/edit`);
  };

  // Open mint modal
  const handleOpenMintModal = (nft) => {
    setCurrentNFT(nft);
    setMintData({
      nftId: nft._id,
      recipientAddress: ''
    });
    setShowMintModal(true);
  };

  // Handle NFT submission
  const handleSubmitNFT = async (e) => {
    e.preventDefault();

    try {
      // Handle numeric values
      const submitData = {
        ...formData,
        price: Number(formData.price),
        attributes: formData.attributes.map(attr => ({
          ...attr,
          value: typeof attr.value === 'string' && !isNaN(attr.value) ? Number(attr.value) : attr.value
        }))
      };

      if (currentNFT) {
        // Update existing NFT
        await api.put(API_ENDPOINTS.admin.nft.updateNFT(currentNFT._id), submitData);
        toast.success('NFT updated successfully');
      } else {
        // Create new NFT
        await api.post(API_ENDPOINTS.admin.nft.createNFT, submitData);
        toast.success('NFT created successfully');
      }

      // Refresh NFTs
      fetchNFTs();

      // Close modal and reset form
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error submitting NFT:', error);
      toast.error('Failed to save NFT');
    }
  };

  // Handle NFT deletion
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this NFT? This action cannot be undone.')) {
      try {
        await api.delete(API_ENDPOINTS.admin.nft.deleteNFT(id));
        toast.success('NFT deleted successfully');
        fetchNFTs();
      } catch (error) {
        console.error('Error deleting NFT:', error);
        toast.error('Failed to delete NFT');
      }
    }
  };

  // Handle NFT minting
  const handleMint = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post(API_ENDPOINTS.admin.nft.mint, mintData);
      toast.success('NFT minted successfully');

      // Refresh NFTs
      fetchNFTs();

      // Close modal
      setShowMintModal(false);
    } catch (error) {
      console.error('Error minting NFT:', error);
      toast.error('Failed to mint NFT');
    }
  };

  // Filter NFTs based on search term and selected collection
  const filteredNFTs = nfts && nfts.length > 0
    ? nfts.filter(nft => {
        const matchesSearch = nft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             nft.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCollection = selectedCollection
          ? (nft.collectionId && (
              (typeof nft.collectionId === 'string' && nft.collectionId === selectedCollection) ||
              (nft.collectionId._id && nft.collectionId._id === selectedCollection)
            ))
          : true;
        return matchesSearch && matchesCollection;
      })
    : [];

  return (
    <div>
      {/* NFTs Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">NFTs</h2>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200 flex items-center"
          >
            <FaPlus className="mr-2" /> Create NFT
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Search NFTs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <select
              value={selectedCollection}
              onChange={(e) => setSelectedCollection(e.target.value)}
              className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Collections</option>
              {collections && collections.length > 0 ? collections.map(collection => (
                <option key={collection._id} value={collection._id}>
                  {collection.name}
                </option>
              )) : null}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredNFTs.length === 0 ? (
              <div className="col-span-3 text-center py-10 text-gray-400">
                No NFTs found
              </div>
            ) : (
              filteredNFTs.map(nft => (
                <div key={nft._id} className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
                  <div className="aspect-square relative">
                    <img
                      src={nft.image}
                      alt={nft.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/logo.png";
                      }}
                    />
                    <div className="absolute top-2 right-2 bg-gray-900 bg-opacity-75 px-2 py-1 rounded-full text-xs">
                      {nft.rarity}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gray-900 bg-opacity-75 p-2">
                      <p className="text-sm font-semibold truncate">{nft.name}</p>
                      <p className="text-xs text-gray-300 truncate">
                        {nft.collectionId && typeof nft.collectionId === 'object' && nft.collectionId.name
                          ? nft.collectionId.name
                          : 'Collection'}
                      </p>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <FaEthereum className="mr-1 text-gray-400" />
                        <span>{nft.price}</span>
                      </div>
                      <div className={`text-xs px-2 py-1 rounded-full ${
                        nft.status === 'minted' ? 'bg-green-900 text-green-300' :
                        nft.status === 'transferred' ? 'bg-blue-900 text-blue-300' :
                        nft.status === 'sold' ? 'bg-purple-900 text-purple-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {nft.status.charAt(0).toUpperCase() + nft.status.slice(1)}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {nft.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleEdit(nft)}
                            className="flex-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs transition duration-200 flex items-center justify-center"
                          >
                            <FaEdit className="mr-1" /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(nft._id)}
                            className="flex-1 px-2 py-1 bg-red-900 hover:bg-red-800 text-red-300 rounded-lg text-xs transition duration-200 flex items-center justify-center"
                          >
                            <FaTrash className="mr-1" /> Delete
                          </button>
                          <button
                            onClick={() => handleOpenMintModal(nft)}
                            className="flex-1 px-2 py-1 bg-green-900 hover:bg-green-800 text-green-300 rounded-lg text-xs transition duration-200 flex items-center justify-center"
                          >
                            <FaCoins className="mr-1" /> Mint
                          </button>
                        </>
                      )}
                      {nft.status !== 'draft' && (
                        <>
                          <button
                            onClick={() => handleEdit(nft)}
                            className="flex-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs transition duration-200 flex items-center justify-center"
                          >
                            <FaEdit className="mr-1" /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(nft._id)}
                            className="flex-1 px-2 py-1 bg-red-900 hover:bg-red-800 text-red-300 rounded-lg text-xs transition duration-200 flex items-center justify-center"
                          >
                            <FaTrash className="mr-1" /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {currentNFT ? 'Edit NFT' : 'Create NFT'}
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmitNFT}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Collection</label>
                <select
                  name="collectionId"
                  value={formData.collectionId}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={currentNFT && currentNFT.status !== 'draft'}
                >
                  <option value="">Select Collection</option>
                  {collections && collections.length > 0 ? collections.map(collection => (
                    <option key={collection._id} value={collection._id}>
                      {collection.name}
                    </option>
                  )) : null}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={currentNFT && currentNFT.status !== 'draft'}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={currentNFT && currentNFT.status !== 'draft'}
                ></textarea>
              </div>

              <div className="mb-4">
                <ImageUploader
                  imageUrl={formData.image}
                  onImageUploaded={(imageUrl) => {
                    setFormData({
                      ...formData,
                      image: imageUrl
                    });
                  }}
                  uploadEndpoint={API_ENDPOINTS.admin.nft.uploadNFTImage}
                  label="NFT Image"
                  aspectRatio="1/1"
                  disabled={currentNFT && currentNFT.status !== 'draft'}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Rarity</label>
                  <select
                    name="rarity"
                    value={formData.rarity}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={currentNFT && currentNFT.status !== 'draft'}
                  >
                    <option value="Common">Common</option>
                    <option value="Uncommon">Uncommon</option>
                    <option value="Rare">Rare</option>
                    <option value="Epic">Epic</option>
                    <option value="Legendary">Legendary</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Price</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={currentNFT && currentNFT.status !== 'draft'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={currentNFT && currentNFT.status !== 'draft'}
                  >
                    <option value="ETH">ETH</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="minted">Minted</option>
                    <option value="transferred">Transferred</option>
                    <option value="sold">Sold</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    NFTs with status "minted", "transferred", or "sold" will appear on the frontend.
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-400">Attributes</label>
                  {(!currentNFT || currentNFT.status === 'draft') && (
                    <button
                      type="button"
                      onClick={handleAddAttribute}
                      className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-lg"
                    >
                      + Add Attribute
                    </button>
                  )}
                </div>
                {formData.attributes.map((attr, index) => (
                  <div key={index} className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={attr.trait_type}
                      onChange={(e) => handleAttributeChange(index, 'trait_type', e.target.value)}
                      placeholder="Trait name"
                      className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={currentNFT && currentNFT.status !== 'draft'}
                    />
                    <input
                      type="number"
                      value={attr.value}
                      onChange={(e) => handleAttributeChange(index, 'value', e.target.value)}
                      placeholder="Value"
                      className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={currentNFT && currentNFT.status !== 'draft'}
                    />
                    {(!currentNFT || currentNFT.status === 'draft') && (
                      <button
                        type="button"
                        onClick={() => handleRemoveAttribute(index)}
                        className="px-2 py-1 bg-red-900 hover:bg-red-800 text-red-300 rounded-lg"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {(!currentNFT || currentNFT.status === 'draft') && (
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200 flex items-center"
                  >
                    <FaCube className="mr-2" />
                    {currentNFT ? 'Update NFT' : 'Create NFT'}
                  </button>
                </div>
              )}

              {currentNFT && currentNFT.status !== 'draft' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition duration-200"
                  >
                    Close
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Mint Modal */}
      {showMintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Mint NFT</h2>
              <button
                onClick={() => setShowMintModal(false)}
                className="text-gray-400 hover:text-white"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleMint}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">NFT</label>
                <input
                  type="text"
                  value={currentNFT?.name}
                  disabled
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg"
                />
                <input type="hidden" name="nftId" value={mintData.nftId} />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Recipient Address (Optional)</label>
                <input
                  type="text"
                  name="recipientAddress"
                  value={mintData.recipientAddress}
                  onChange={handleMintInputChange}
                  placeholder="0x... (leave empty to mint to admin wallet)"
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  If left empty, the NFT will be minted to the admin wallet.
                </p>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowMintModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200 flex items-center"
                >
                  <FaCoins className="mr-2" />
                  Mint NFT
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNFTs;
